import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ThemeToggle } from '../components/ThemeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { LogoBrain, FlowFeedLogo } from '../components/Logo';
import AIChatbot from '../components/AIChatbot';
import { 
  ArrowRight, 
  CheckCircle2, 
  Store,
  Cigarette,
  Smartphone,
  Shirt,
  UtensilsCrossed,
  Pill,
  ShoppingBasket,
  Scissors,
  Wrench,
  Truck,
  BarChart3,
  Brain,
  Package,
  Shield,
  Zap,
  CreditCard,
  Users,
  Receipt,
  Clock,
  Star,
  ChevronRight,
  Play,
  ScanBarcode,
  Calculator,
  Fuel,
  TrendingUp,
  Lock,
  Check,
  Building2,
  DollarSign,
  MapPin,
  Monitor,
  Laptop,
  Phone
} from 'lucide-react';

// Business category data with images from design guidelines
const BUSINESS_CATEGORIES = [
  { 
    id: "retail_store", 
    name: "Retail Store", 
    icon: Store, 
    description: "General retail & merchandise",
    image: "https://images.unsplash.com/photo-1715635846028-f162249377c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXRhaWwlMjBzdG9yZSUyMG93bmVyJTIwdXNpbmclMjB0YWJsZXQlMjBQT1N8ZW58MHx8fHwxNzcyNTI1Mzc0fDA&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["Fast POS Checkout", "Inventory Tracking", "Customer Loyalty"]
  },
  { 
    id: "gas_station", 
    name: "Gas Station", 
    icon: Fuel, 
    description: "Fuel & convenience store",
    image: "https://images.unsplash.com/photo-1567581935884-3349723552ca?w=400",
    features: ["Fuel Pump Integration", "Prepay/Postpay", "Tank Monitoring"]
  },
  { 
    id: "cash_register", 
    name: "AI Cash Register", 
    icon: Calculator, 
    description: "Smart checkout & payments",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400",
    features: ["AI-Powered Checkout", "Multi-Payment Support", "Smart Analytics"]
  },
  { 
    id: "smoke_shop", 
    name: "Smoke Shop", 
    icon: Cigarette, 
    description: "Tobacco & vape retail",
    image: "https://images.unsplash.com/photo-1738106380642-516ce53742db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxzbW9rZSUyMHNob3AlMjBpbnRlcmlvciUyMG1vZGVybiUyMHJldGFpbHxlbnwwfHx8fDE3NzI1MjUzNzV8MA&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["21+ Age Verification", "Tobacco Tax Tracking", "Compliance Reports"]
  },
  { 
    id: "mobile_electronics", 
    name: "Mobile & Electronics", 
    icon: Smartphone, 
    description: "Phones, repairs & accessories",
    image: "https://images.unsplash.com/photo-1758631279366-8e8aeaf94082?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxtb2JpbGUlMjBwaG9uZSUyMHJlcGFpciUyMHNob3AlMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3NzI1MjUzNzZ8MA&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["IMEI Tracking", "Repair Management", "Warranty Tracking"]
  },
  { 
    id: "clothing_fashion", 
    name: "Clothing & Fashion", 
    icon: Shirt, 
    description: "Apparel & accessories",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400",
    features: ["Size/Color Variants", "Season Management", "Style Analytics"]
  },
  { 
    id: "restaurant_cafe", 
    name: "Restaurant & Cafe", 
    icon: UtensilsCrossed, 
    description: "Food service & dining",
    image: "https://images.unsplash.com/photo-1708517194326-6077b788f04b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBidXN5JTIwaGFwcHl8ZW58MHx8fHwxNzcyNTI1Mzc3fDA&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["Table Management", "Kitchen Display", "Split Bills"]
  },
  { 
    id: "pharmacy", 
    name: "Pharmacy", 
    icon: Pill, 
    description: "Health & wellness retail",
    image: "https://images.unsplash.com/photo-1735448213858-6bdfdf78967a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBwaGFybWFjeSUyMGludGVyaW9yJTIwY2xlYW4lMjByZXRhaWx8ZW58MHx8fHwxNzcyNTI1Mzk3fDA&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["Rx Management", "Expiry Tracking", "Insurance Integration"]
  },
  { 
    id: "grocery_convenience", 
    name: "Grocery & Convenience", 
    icon: ShoppingBasket, 
    description: "Food & daily essentials",
    image: "https://images.unsplash.com/photo-1760463921642-eef64776c3bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBncm9jZXJ5JTIwc3RvcmUlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3NzI1MjUzOTV8MA&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["Quick Checkout", "Perishable Tracking", "Reorder Alerts"]
  },
  { 
    id: "salon_spa", 
    name: "Salon & Spa", 
    icon: Scissors, 
    description: "Beauty & wellness services",
    image: "https://images.unsplash.com/photo-1760862652442-e8ff7ebdd2f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBiZWF1dHklMjBzYWxvbiUyMGludGVyaW9yJTIwbHV4dXJ5fGVufDB8fHx8MTc3MjUyNTM5Nnww&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["Appointment Booking", "Staff Scheduling", "Service Packages"]
  },
  { 
    id: "auto_hardware", 
    name: "Auto & Hardware", 
    icon: Wrench, 
    description: "Auto parts & tools",
    image: "https://images.unsplash.com/photo-1679434050541-5f07c8d28683?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxhdXRvJTIwcGFydHMlMjBzdG9yZSUyMGludGVyaW9yJTIwcmV0YWlsfGVufDB8fHx8MTc3MjUyNTM5OHww&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["Part Lookup", "Vehicle Compatibility", "Warranty Claims"]
  },
  { 
    id: "wholesale_distribution", 
    name: "Wholesale & Distribution", 
    icon: Truck, 
    description: "Bulk sales & distribution",
    image: "https://images.unsplash.com/photo-1698466632366-09fa1d925de6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBncm9jZXJ5JTIwc3RvcmUlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3NzI1MjUzOTV8MA&ixlib=rb-4.1.0&q=85&w=400&q=60",
    features: ["B2B Pricing", "Route Planning", "Bulk Discounts"]
  }
];

// Pricing plans
const PRICING_PLANS = [
  {
    id: "free_trial",
    name: "Free Trial",
    price: 0,
    period: "7 days",
    description: "Try FlowFeed risk-free",
    features: ["Basic POS Terminal", "Up to 100 products", "1 User", "Email support"],
    cta: "Start Free Trial",
    popular: false
  },
  {
    id: "starter",
    name: "Starter",
    price: 29,
    period: "month",
    description: "Perfect for small stores",
    features: ["Full POS Terminal", "Up to 200 products", "3 Users", "Basic CRM", "Invoicing", "Email support"],
    cta: "Get Started",
    popular: false
  },
  {
    id: "business",
    name: "Business",
    price: 79,
    period: "month",
    description: "For growing businesses",
    features: ["Everything in Starter", "Unlimited products", "10 Users", "Full CRM Suite", "HR & Payroll", "Procurement", "Priority support"],
    cta: "Go Business",
    popular: true
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: null,
    period: "month",
    description: "For large operations",
    features: ["Everything in Business", "Unlimited Users", "Multi-location", "Custom integrations", "Dedicated support", "SLA guarantee"],
    cta: "Contact Us",
    popular: false
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    quote: "FlowFeed transformed our smoke shop operations. The age verification and tax tracking saves us hours every week.",
    author: "Mike Thompson",
    role: "Owner, Cloud Nine Smoke Shop",
    location: "Austin, TX",
    avatar: "MT",
    rating: 5
  },
  {
    quote: "Finally, a POS system that understands retail. The barcode scanning and inventory alerts are game-changers.",
    author: "Sarah Martinez",
    role: "Manager, Urban Style Boutique",
    location: "Los Angeles, CA",
    avatar: "SM",
    rating: 5
  },
  {
    quote: "We've increased our checkout speed by 40% since switching to FlowFeed. Our customers love the fast service.",
    author: "David Chen",
    role: "Owner, Chen's Electronics",
    location: "New York, NY",
    avatar: "DC",
    rating: 5
  }
];

const RetailLandingPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  // Dynamic headline based on selected category
  const getHeadline = () => {
    if (selectedCategory) {
      const cat = BUSINESS_CATEGORIES.find(c => c.id === selectedCategory);
      return cat ? cat.name : "Your Business";
    }
    return "US Retail";
  };

  // Handle category selection and navigate to signup
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    // Navigate to signup with pre-selected category
    navigate(`/signup?business_type=${categoryId}`);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white overflow-x-hidden">
      {/* Subtle Background Pattern */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#10b98108_1px,transparent_1px),linear-gradient(to_bottom,#10b98108_1px,transparent_1px)] bg-[size:48px_48px]"></div>
      </div>

      {/* Navbar */}
      <nav className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <FlowFeedLogo size="default" showTagline={true} />
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600 dark:text-slate-400">
            <a href="#categories" className="hover:text-slate-900 dark:hover:text-white transition-colors" data-testid="nav-categories">
              Industries
            </a>
            <Link to="/gas-station" className="flex items-center gap-1 text-orange-500 hover:text-orange-600 transition-colors" data-testid="nav-gas-station">
              <Fuel className="h-4 w-4" />
              Gas Stations
            </Link>
            <a href="#features" className="hover:text-slate-900 dark:hover:text-white transition-colors" data-testid="nav-features">
              Features
            </a>
            <a href="#pricing" className="hover:text-slate-900 dark:hover:text-white transition-colors" data-testid="nav-pricing">
              Pricing
            </a>
            <a href="#testimonials" className="hover:text-slate-900 dark:hover:text-white transition-colors" data-testid="nav-testimonials">
              Reviews
            </a>
          </div>
          
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <Link to="/login">
              <Button variant="ghost" className="font-medium" data-testid="nav-login">Login</Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25" data-testid="nav-signup">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 right-[10%] w-72 h-72 bg-emerald-500/10 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-20 left-[10%] w-72 h-72 bg-blue-500/10 rounded-full blur-[100px]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"></span>
                </span>
                Trusted by 500+ US Retailers
              </div>
              
              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                <span className="text-slate-900 dark:text-white">The Retail OS for</span>
                <br />
                <AnimatePresence mode="wait">
                  <motion.span
                    key={selectedCategory || 'default'}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent"
                  >
                    {getHeadline()}
                  </motion.span>
                </AnimatePresence>
              </h1>

              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-xl leading-relaxed">
                Still using a basic cash register? <strong className="text-slate-900 dark:text-white">You're losing money.</strong> Your old POS can't think — FlowFeed can. AI-powered insights, smart inventory, voice commands & industry-specific interfaces customized for YOUR business.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/signup">
                  <Button size="lg" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl shadow-emerald-500/30 text-base px-8 h-12" data-testid="hero-cta-primary">
                    Start Free 14-Day Trial <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 text-base h-12" data-testid="hero-cta-demo">
                  <Play className="mr-2 h-5 w-5" /> Watch Demo
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">PCI Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">No Setup Fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">24/7 Support</span>
                </div>
              </div>
            </motion.div>

            {/* Right: POS Hardware Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Main POS Hardware Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/20 dark:shadow-black/40">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/9f5fe124-0421-4881-9c48-b04ed7d2f258/images/5bfc11b4a44f27edff303aa31ce45c17eefbff321658450de6feadc9a49f06a1.png" 
                  alt="FlowFeed POS System - Complete retail point of sale with dual displays, cash drawer, thermal printer and barcode scanner"
                  className="w-full h-auto object-cover"
                />
                
                {/* Overlay gradient for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              </div>

              {/* Hardware Labels */}
              <div className="absolute top-4 right-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-1">Complete Setup</div>
                <div className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500"></div>
                    15" Cashier Display
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500"></div>
                    10" Customer Screen
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500"></div>
                    Cash Drawer
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500"></div>
                    Thermal Printer
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500"></div>
                    Barcode Scanner
                  </div>
                </div>
              </div>

              {/* AI Powered Badge */}
              <div className="absolute -bottom-4 -left-4 bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-xl border border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Brain className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-white">AI Powered</div>
                    <div className="text-xs text-slate-500">Smart insights & automation</div>
                  </div>
                </div>
              </div>

              {/* Online Status Badge */}
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-emerald-500/90 text-white px-3 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm">
                <div className="h-2 w-2 rounded-full bg-white animate-pulse"></div>
                FlowFeed POS
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Business Category Selector Section */}
      <section id="categories" className="py-20 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 mb-4"
            >
              <Store className="h-4 w-4" />
              Choose Your Industry
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Built for <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600">Every Retail Type</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Select your business type and get a system pre-configured for your industry
            </p>
          </div>

          {/* Category Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {BUSINESS_CATEGORIES.map((category, idx) => {
              const Icon = category.icon;
              const isHovered = hoveredCategory === category.id;
              
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03, duration: 0.3 }}
                  viewport={{ once: true }}
                  onMouseEnter={() => setHoveredCategory(category.id)}
                  onMouseLeave={() => setHoveredCategory(null)}
                  onClick={() => handleCategorySelect(category.id)}
                  className="relative group cursor-pointer"
                  data-testid={`category-${category.id}`}
                >
                  <div className={`relative overflow-hidden rounded-2xl border-2 transition-all duration-300 ${
                    isHovered 
                      ? 'border-emerald-500 shadow-xl shadow-emerald-500/20 scale-[1.02]' 
                      : 'border-slate-200 dark:border-slate-700 hover:border-emerald-500/50'
                  }`}>
                    {/* Image */}
                    <div className="aspect-[4/3] overflow-hidden">
                      <img 
                        src={category.image} 
                        alt={category.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      {/* Overlay */}
                      <div className={`absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent transition-opacity duration-300 ${
                        isHovered ? 'opacity-90' : 'opacity-70'
                      }`}></div>
                    </div>
                    
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${
                          isHovered ? 'bg-emerald-500' : 'bg-white/20'
                        }`}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <h3 className="font-semibold text-white text-sm">{category.name}</h3>
                      </div>
                      
                      {/* Features - Show on hover */}
                      <AnimatePresence>
                        {isHovered && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-2 space-y-1"
                          >
                            {category.features.map((feature, i) => (
                              <div key={i} className="flex items-center gap-1.5 text-xs text-emerald-300">
                                <Check className="h-3 w-3" />
                                {feature}
                              </div>
                            ))}
                            {/* Special "Learn More" link for Gas Station */}
                            {category.id === 'gas_station' && (
                              <Link 
                                to="/gas-station" 
                                className="inline-flex items-center gap-1 mt-2 text-xs text-orange-400 hover:text-orange-300 font-medium"
                                onClick={(e) => e.stopPropagation()}
                              >
                                See Gas Station Demo <ArrowRight className="h-3 w-3" />
                              </Link>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Arrow indicator */}
                    <div className={`absolute top-3 right-3 h-8 w-8 rounded-full bg-white/10 flex items-center justify-center transition-all ${
                      isHovered ? 'bg-emerald-500 scale-100' : 'scale-0'
                    }`}>
                      <ArrowRight className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <Link to="/signup">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="categories-cta">
                Get Started with Your Industry
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* AI Smart Fraud Prevention Section */}
      <section className="py-16 bg-gradient-to-br from-red-950 via-slate-900 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-20 w-64 h-64 border border-red-500 rounded-full"></div>
          <div className="absolute bottom-10 left-20 w-40 h-40 border border-red-500 rounded-full"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/50 bg-red-500/20 px-4 py-1.5 text-sm font-medium text-red-400 mb-4">
                <Shield className="h-4 w-4" />
                AI Smart Fraud Prevention
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Stop Fraud <span className="text-red-400">Before It Costs You</span>
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                US C-stores lose an average of $15,000 per year to shrinkage and fraud. 
                FlowFeed's AI-powered monitoring detects suspicious patterns automatically.
              </p>
              <div className="space-y-4">
                {[
                  { icon: '1', title: 'Void & Refund Alerts', desc: 'Get notified when voids exceed threshold per shift' },
                  { icon: '2', title: 'Cash Drawer Monitoring', desc: 'No-sale drawer opens are logged with cashier ID and timestamp' },
                  { icon: '3', title: 'Over/Short Reports', desc: 'Track expected vs actual cash per cashier across every shift' },
                  { icon: '4', title: 'Discount Abuse Detection', desc: 'Flags cashiers giving 50%+ more discounts than average' },
                  { icon: '5', title: 'Suspicious Activity Dashboard', desc: 'Weekly summary of flagged cashiers with evidence' },
                ].map((item, idx) => (
                  <motion.div key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="h-8 w-8 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 font-bold text-sm shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{item.title}</h4>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {/* Mock Dashboard Preview */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-3 w-3 rounded-full bg-red-500"></div>
                  <span className="text-sm font-semibold text-red-400">2 Alerts Flagged This Week</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div>
                      <p className="font-medium text-sm">Broadway Store - Register 1</p>
                      <p className="text-xs text-slate-400">7 voids ($342) — above threshold</p>
                    </div>
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full font-semibold">FLAGGED</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                    <div>
                      <p className="font-medium text-sm">Main St Store - Register 2</p>
                      <p className="text-xs text-slate-400">$1,247 sales — 0 voids</p>
                    </div>
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full font-semibold">CLEAN</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div>
                      <p className="font-medium text-sm">Highway 41 Store - Register 1</p>
                      <p className="text-xs text-slate-400">12 discounts — 60% above average</p>
                    </div>
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full font-semibold">FLAGGED</span>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <p className="text-xs text-slate-400 mb-2">Cash Drawer Variance — Last 7 Days</p>
                <div className="flex items-end gap-2 h-16">
                  {[-2.50, 0, -15.75, 0, -0.25, -45.00, 0].map((v, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center">
                      <div className={`w-full rounded-t ${v < -5 ? 'bg-red-500' : v < 0 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ height: `${Math.max(4, Math.abs(v) * 1.2)}px` }}></div>
                      <span className="text-[9px] text-slate-500 mt-1">{['M','T','W','T','F','S','S'][i]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free Tools Section */}
      <section className="py-16 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Free Tools for Store Owners
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Use these free calculators and compliance tools — no signup required.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Tobacco Tax Calculator',
                desc: 'Calculate state excise tax for all 50 states. Know exactly what you owe per carton.',
                icon: '1',
                color: 'emerald',
                link: '/tools/tobacco-tax-calculator',
                tags: ['All 50 States', '2025 Rates']
              },
              {
                title: 'Profit Margin Calculator',
                desc: 'Calculate margins, markup, and projected earnings for any product category.',
                icon: '2',
                color: 'blue',
                link: '/tools/profit-margin-calculator',
                tags: ['Benchmarks', 'Projections']
              },
              {
                title: 'Age Verification Checker',
                desc: 'Check if your store meets state compliance requirements. Avoid costly FDA fines.',
                icon: '3',
                color: 'red',
                link: '/tools/age-verification-checker',
                tags: ['State Laws', 'Penalties']
              },
            ].map((tool, idx) => (
              <motion.a
                key={idx}
                href={tool.link}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 hover:border-emerald-500/50 hover:shadow-lg transition-all"
              >
                <div className={`h-12 w-12 rounded-xl bg-${tool.color}-100 dark:bg-${tool.color}-950 flex items-center justify-center mb-4 text-${tool.color}-600 font-bold text-xl`}>
                  {tool.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 group-hover:text-emerald-600 transition-colors">
                  {tool.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{tool.desc}</p>
                <div className="flex gap-2">
                  {tool.tags.map((tag, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-full">{tag}</span>
                  ))}
                </div>
                <p className="mt-4 text-sm text-emerald-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  Try Free <ArrowRight className="h-4 w-4" />
                </p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-4"
            >
              <Zap className="h-4 w-4" />
              Powerful Features
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Everything You Need to <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600">Run Your Store</span>
            </h2>
          </div>

          {/* Bento Grid Features */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* POS & Cash Register - Large */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl p-8 text-white relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative">
                <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                  <ScanBarcode className="h-7 w-7" />
                </div>
                <h3 className="text-2xl font-bold mb-2">POS & Cash Register</h3>
                <p className="text-emerald-100 text-lg mb-6 max-w-lg">
                  Fast, offline-ready, touch-optimized. Process sales in seconds with barcode scanning and smart search.
                </p>
                <div className="flex flex-wrap gap-3">
                  {["Barcode Scanning", "Offline Mode", "Quick Keys", "Split Payments"].map((f, i) => (
                    <span key={i} className="bg-white/20 px-3 py-1.5 rounded-full text-sm font-medium">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Inventory */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-slate-100 dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800"
            >
              <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Inventory Management</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Real-time stock tracking with low stock alerts and automatic reorder points.
              </p>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Stock alerts</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Multi-location</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Barcode labels</li>
              </ul>
            </motion.div>

            {/* AI Insights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl p-6 text-white relative overflow-hidden"
            >
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl translate-y-1/2 translate-x-1/2"></div>
              <div className="relative">
                <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                  <Brain className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">AI-Powered Insights</h3>
                <p className="text-blue-100 mb-4">
                  Predict sales trends, optimize inventory, and get smart recommendations.
                </p>
                <div className="flex items-center gap-2 bg-white/20 rounded-lg p-3">
                  <TrendingUp className="h-5 w-5" />
                  <span className="text-sm font-medium">Sales up 23% this week</span>
                </div>
              </div>
            </motion.div>

            {/* US Tax Compliance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-slate-100 dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800"
            >
              <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
                <Calculator className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">50-State Tax Compliance</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Automatic sales tax calculation for all US states. Always accurate, always compliant.
              </p>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="h-4 w-4" />
                <span>State + Local tax rates built-in</span>
              </div>
            </motion.div>

            {/* Employee Management */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-slate-100 dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800"
            >
              <div className="h-12 w-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Employee Management</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Track shifts, manage permissions, and monitor performance all in one place.
              </p>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Time clock</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Role permissions</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Hardware Compatibility Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 border border-white rounded-full"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-500/50 bg-emerald-500/20 px-4 py-1.5 text-sm font-medium text-emerald-400 mb-4"
            >
              <Monitor className="h-4 w-4" />
              Universal Compatibility
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Works on <span className="text-emerald-400">Any Hardware</span> You Already Have
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              No need to buy new equipment. FlowFeed runs on iOS, Android & Windows devices. 
              Just install our software and transform your existing hardware into a powerful AI-driven POS.
            </p>
          </div>

          {/* Platform Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* iOS */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-emerald-500/50 transition-colors"
            >
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">iOS / iPadOS</h3>
              <p className="text-slate-300 text-sm mb-4">iPad, iPhone - Perfect for modern retail setups with Apple ecosystem</p>
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" />
                App Store Ready
              </div>
            </motion.div>

            {/* Android */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-emerald-500/50 transition-colors"
            >
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-1.4-.59-2.96-.92-4.47-.92s-3.07.33-4.47.92L5.65 5.67c-.19-.29-.54-.38-.83-.22-.31.16-.43.54-.26.85l1.84 3.18C3.35 11.14 1.37 14.34 1 18h22c-.37-3.66-2.35-6.86-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Android</h3>
              <p className="text-slate-300 text-sm mb-4">Tablets, phones, dedicated POS terminals - flexible & affordable options</p>
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" />
                Play Store Ready
              </div>
            </motion.div>

            {/* Windows */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-emerald-500/50 transition-colors"
            >
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 12V6.75l6-1.32v6.48L3 12zm17-9v8.75l-10 .15V5.21L20 3zM3 13l6 .09v6.81l-6-1.15V13zm7 .25l10 .15V21l-10-1.91V13.25z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Windows</h3>
              <p className="text-slate-300 text-sm mb-4">PCs, laptops, all-in-one touchscreens - use your existing computers</p>
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" />
                Desktop & Web App
              </div>
            </motion.div>
          </div>

          {/* Already Have Hardware Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-emerald-600/20 to-blue-600/20 rounded-2xl p-8 border border-emerald-500/30 text-center"
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                  <Laptop className="h-8 w-8 text-emerald-400" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Already Have Hardware?</h3>
                  <p className="text-slate-300">Just install FlowFeed and start selling in minutes!</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a href="mailto:support@flowfeed.com">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-6">
                    <Phone className="h-4 w-4 mr-2" /> Contact Us for Details
                  </Button>
                </a>
                <Link to="/signup">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    Get Started Free
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-4"
            >
              <DollarSign className="h-4 w-4" />
              Simple Pricing
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Plans That <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600">Grow With You</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Start free, upgrade when you're ready. No hidden fees.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRICING_PLANS.map((plan, idx) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className={`relative rounded-3xl p-6 ${
                  plan.popular 
                    ? 'bg-gradient-to-b from-emerald-500 to-emerald-600 text-white shadow-xl shadow-emerald-500/25' 
                    : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800'
                }`}
                data-testid={`pricing-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}

                <div className="mb-6">
                  <h3 className={`text-xl font-bold mb-1 ${plan.popular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm ${plan.popular ? 'text-emerald-100' : 'text-slate-500'}`}>
                    {plan.description}
                  </p>
                </div>

                <div className="mb-6">
                  {plan.price !== null ? (
                    <>
                      <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                        ${plan.price}
                      </span>
                      <span className={`text-sm ${plan.popular ? 'text-emerald-100' : 'text-slate-500'}`}>
                        /{plan.period}
                      </span>
                    </>
                  ) : (
                    <span className={`text-2xl font-bold ${plan.popular ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                      Contact Us
                    </span>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${plan.popular ? 'text-emerald-100' : 'text-slate-600 dark:text-slate-400'}`}>
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${plan.popular ? 'text-white' : 'text-emerald-500'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link to={`/signup?plan=${plan.id}`}>
                  <Button 
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-white text-emerald-600 hover:bg-emerald-50' 
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                    data-testid={`pricing-cta-${plan.id}`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-sm font-medium text-amber-600 dark:text-amber-400 mb-4"
            >
              <Star className="h-4 w-4" />
              Customer Stories
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Loved by <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600">Retailers Nationwide</span>
            </h2>
          </div>

          {/* Testimonial Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-lg"
                data-testid={`testimonial-${idx}`}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-slate-700 dark:text-slate-300 mb-6">
                  "{testimonial.quote}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-white font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-white">{testimonial.author}</div>
                    <div className="text-sm text-slate-500">{testimonial.role}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {testimonial.location}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 via-emerald-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:40px_40px]"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-[150px]"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Retail Business?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join 500+ US retailers who've switched to FlowFeed. Start your free trial today - no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup">
                <Button size="lg" className="h-14 px-8 bg-white text-emerald-600 hover:bg-emerald-50 shadow-xl" data-testid="final-cta-primary">
                  Start Your Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="h-14 px-8 border-white/30 text-white hover:bg-white/10" data-testid="final-cta-secondary">
                Schedule a Demo
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <FlowFeedLogo size="small" showTagline={true} className="[&_span]:!text-white [&_.text-slate-800]:!text-white [&_.text-slate-500]:!text-slate-400" />
              </div>
              <p className="text-sm">
                The AI-powered retail platform for American businesses.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#categories" className="hover:text-white transition-colors">Industries</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/" className="hover:text-white transition-colors">About</Link></li>
                <li><Link to="/" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; 2025 FlowFeed. All rights reserved. Made with ❤️ for US Retailers.</p>
          </div>
        </div>
      </footer>

      {/* AI Chatbot */}
      <AIChatbot variant="landing" />
    </div>
  );
};

export default RetailLandingPage;
